import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDropdownPosition, useDropdownLifecycle } from '../../hooks';
import type { DropdownVariant, DropdownSize, DropdownBorderRadius } from '../../types';

const cx = (...classes: (string | boolean | undefined)[]) => 
  classes.filter(Boolean).join(' ');

export interface DropdownContainerProps {
  isOpen: boolean;
  onClose?: () => void;
  anchorRef?: React.RefObject<HTMLElement>;
  position?: 'left' | 'right';
  direction?: 'up' | 'down';
  variant?: DropdownVariant;
  size?: DropdownSize;
  borderRadius?: DropdownBorderRadius;
  minWidth?: string;
  maxWidth?: string;
  maxHeight?: string;
  className?: string;
  children: React.ReactNode;
  closeOnScroll?: boolean;
  closeOnResize?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  offsetY?: number;
  zIndex?: number;
  usePortal?: boolean;
}

export function DropdownContainer({
  isOpen,
  onClose,
  anchorRef,
  position = 'left',
  direction = 'down',
  variant = 'default',
  size = 'medium',
  borderRadius = 'full',
  minWidth,
  maxWidth,
  maxHeight,
  className,
  children,
  closeOnScroll = true,
  closeOnResize = false,
  onClick,
  offsetY = 4,
  zIndex,
  usePortal = true
}: DropdownContainerProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const { position: dropdownPosition, actualDirection } = useDropdownPosition({
    isOpen,
    anchorRef,
    dropdownRef,
    position,
    direction,
    offsetY
  });
  
  useDropdownLifecycle({
    isOpen,
    onClose,
    dropdownRef,
    anchorRef,
    closeOnScroll,
    closeOnResize
  });
  
  if (!isOpen || !dropdownPosition) return null;
  
  const dropdownClasses = cx(
    'core-dropdown-container',
    size && `core-dropdown-container--${size}`,
    variant && `core-dropdown-container--${variant}`,
    borderRadius !== 'full' && `core-dropdown-container--radius-${borderRadius}`,
    actualDirection === 'up' ? 'ui-dropdown--opening-up' : 'ui-dropdown--opening-down',
    className
  );
  
  const dropdownStyles: React.CSSProperties = {
    position: 'fixed',
    ...(dropdownPosition?.top !== undefined 
      ? { top: dropdownPosition.top }
      : dropdownPosition?.bottom !== undefined 
        ? { bottom: dropdownPosition.bottom }
        : { top: 0 }),
    ...(dropdownPosition?.left !== undefined 
      ? { left: dropdownPosition.left }
      : dropdownPosition?.right !== undefined
        ? { right: dropdownPosition.right }
        : { left: 0 }),
    minWidth,
    maxWidth,
    maxHeight,
    ...(zIndex !== undefined && { zIndex })
  };
  
  const dropdown = (
    <div
      ref={dropdownRef}
      className={dropdownClasses}
      style={dropdownStyles}
      onClick={onClick}
      data-dropdown-portal="true"
    >
      {children}
    </div>
  );

  if (usePortal && typeof document !== 'undefined') {
    return createPortal(dropdown, document.body);
  }
  
  return dropdown;
}

